/**
 * PD-17435 — teams picked in a report's Teams filter. A pick includes the
 * team's sub-teams unless "Include sub-teams" is off, in which case it
 * travels as "=<id>": that team's own members only. Plain ids (every saved
 * filter from before) keep including sub-teams.
 */
export const DIRECT_TEAM_PREFIX = '=';

/** The value a pick travels as. */
export function teamPickToken(teamId: number | string, includeSubTeams: boolean): string {
	const id = String(teamId).replace(DIRECT_TEAM_PREFIX, '');
	return includeSubTeams ? id : `${DIRECT_TEAM_PREFIX}${id}`;
}

export interface TeamPicksModel {
	/** Teams counted with everything beneath them. */
	withSubTeams: Array<number>;
	/** Teams counted on their own. */
	directOnly: Array<number>;
}

/**
 * The picks in a request: numbers, strings, "=<id>" tokens or `{ id }`
 * options. Null when the filter was not sent at all (no pick is not an
 * empty pick).
 */
export function parseTeamPicks(value: unknown): TeamPicksModel | null {
	if (!Array.isArray(value)) {
		return null;
	}
	const picks: TeamPicksModel = { withSubTeams: [], directOnly: [] };
	for (const entry of value) {
		const raw = entry != null && typeof entry === 'object' && 'id' in entry ? (entry as { id: unknown }).id : entry;
		const text = typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw.trim() : '';
		const direct = text.startsWith(DIRECT_TEAM_PREFIX);
		const id = Number(direct ? text.slice(DIRECT_TEAM_PREFIX.length) : text);
		if (text === '' || !Number.isFinite(id)) {
			continue;
		}
		const list = direct ? picks.directOnly : picks.withSubTeams;
		if (!list.includes(id)) {
			list.push(id);
		}
	}
	return picks;
}

/** Every team picked, either way. */
export function allPickedTeamIds(picks: TeamPicksModel): Array<number> {
	return [...new Set([...picks.withSubTeams, ...picks.directOnly])];
}
